import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './EditarPosicoesFinais.css';

/**
 * Página para Admin editar posições finais dos jogadores em rodada aberta
 */

const EditarPosicoesFinais = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [rodada, setRodada] = useState(null);
  const [participacoes, setParticipacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    if (id) {
      carregarDados();
    }
  }, [isAdmin, navigate, id]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      // Carregar rodada
      const rodadaRes = await apiClient.get(`/rodadas/${id}`);
      setRodada(rodadaRes.data);

      if (rodadaRes.data.status !== 'aberta') {
        setError('Esta funcionalidade só está disponível para rodadas abertas.');
        setLoading(false);
        return;
      }

      // Carregar participações
      const confirmacoesRes = await apiClient.get(`/rodadas/${id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      
      // Ordenar por posição (se tiver) ou por nome
      const participacoesOrdenadas = confirmacoes.sort((a, b) => {
        if (a.posicao_final && b.posicao_final) {
          return a.posicao_final - b.posicao_final;
        }
        if (a.posicao_final) return -1;
        if (b.posicao_final) return 1;
        return (a.jogador?.nome || '').localeCompare(b.jogador?.nome || '');
      });

      setParticipacoes(participacoesOrdenadas);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handlePosicaoChange = (jogadorId, novaPosicao) => {
    setParticipacoes(prev => prev.map(p => {
      if (p.jogador_id === jogadorId) {
        return { ...p, posicao_final: novaPosicao ? parseInt(novaPosicao) : null };
      }
      return p;
    }));
    setError('');
    setSuccess('');
  };

  const validarPosicoes = () => {
    const posicoes = participacoes
      .map(p => p.posicao_final)
      .filter(p => p !== null && p !== undefined && p !== '');

    // Verificar duplicatas
    const posicoesUnicas = [...new Set(posicoes)];
    if (posicoes.length !== posicoesUnicas.length) {
      setError('Não podem existir duas posições iguais. Verifique os valores informados.');
      return false;
    }

    // Verificar range
    const maxPosicao = participacoes.length;
    const posicoesInvalidas = posicoes.filter(p => p < 1 || p > maxPosicao);
    if (posicoesInvalidas.length > 0) {
      setError(`Posições devem estar entre 1 e ${maxPosicao}`);
      return false;
    }

    return true;
  };

  const handleSalvar = async (e) => {
    e.preventDefault();

    if (!validarPosicoes()) {
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      // Preparar array de participações com posições
      const participacoesComPosicao = participacoes
        .filter(p => p.posicao_final !== null && p.posicao_final !== undefined && p.posicao_final !== '')
        .map(p => ({
          jogador_id: p.jogador_id,
          posicao_final: parseInt(p.posicao_final),
        }));

      if (participacoesComPosicao.length === 0) {
        setError('Nenhuma posição foi informada.');
        setSalvando(false);
        return;
      }

      await apiClient.put(`/rodadas/${id}/posicoes-finais`, {
        participacoes: participacoesComPosicao,
      });

      setSuccess('Posições atualizadas com sucesso! As pontuações serão recalculadas automaticamente.');
      await carregarDados();
    } catch (err) {
      console.error('Erro ao salvar posições:', err);
      setError(err.response?.data?.error || 'Erro ao salvar posições');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodada) {
    return (
      <div className="container">
        <div className="error">Rodada não encontrada.</div>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-primary">
          Voltar ao Menu
        </button>
      </div>
    );
  }

  if (rodada.status !== 'aberta') {
    return (
      <div className="container">
        <div className="error">Esta funcionalidade só está disponível para rodadas abertas.</div>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-primary">
          Voltar ao Menu
        </button>
      </div>
    );
  }

  return (
    <div className="container editar-posicoes-container">
      <div className="page-header">
        <h1>✏️ Editar Posições Finais</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodada.data).toLocaleDateString('pt-BR')}</p>
        <p><strong>Total de Jogadores:</strong> {participacoes.length}</p>
        <div className="info-box" style={{ marginTop: '10px', padding: '10px', background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
          <p style={{ margin: 0, fontSize: '0.9em' }}>
            <strong>ℹ️ Informação:</strong> As posições serão refletidas no ranking imediatamente após salvar.
          </p>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <form onSubmit={handleSalvar} className="posicoes-form">
        <div className="table-container">
          <table className="posicoes-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Posição Final</th>
                <th>Pontos</th>
              </tr>
            </thead>
            <tbody>
              {participacoes.map((participacao) => (
                <tr key={participacao.jogador_id}>
                  <td className="jogador-nome">
                    {participacao.jogador?.nome || 'Desconhecido'}
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      max={participacoes.length}
                      value={participacao.posicao_final || ''}
                      onChange={(e) => handlePosicaoChange(participacao.jogador_id, e.target.value)}
                      className="posicao-input"
                      placeholder="Ex: 1, 2, 3..."
                    />
                  </td>
                  <td className="pontos">
                    {participacao.posicao_final 
                      ? `Será calculado após salvar`
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            disabled={salvando} 
            className="btn btn-primary"
          >
            {salvando ? 'Salvando...' : 'Salvar Posições'}
          </button>
          <button 
            type="button"
            onClick={() => navigate('/admin/menu')}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
        </div>
      </form>

      <div className="info-box" style={{ marginTop: '20px' }}>
        <p>
          <strong>💡 Dica:</strong> Você pode editar as posições de todos os jogadores de uma vez. 
          As posições devem ser únicas e estar entre 1 e {participacoes.length}.
        </p>
      </div>
    </div>
  );
};

export default EditarPosicoesFinais;
